import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { environment } from '../../environments/environment';

export interface AccountApiError {
  code: number;
  message: string;
  details?: string[];
}

export interface AccountProfile {
  publicId: string;
  email: string;
  name: string | null;
  nickname: string | null;
  emailVerified: boolean;
  productAnalyticsEnabled: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface AccountAddress {
  publicId: string;
  kind: 'shipping' | 'billing' | 'other';
  label: string | null;
  fullName: string | null;
  company: string | null;
  line1: string;
  line2: string | null;
  city: string;
  state: string | null;
  postalCode: string;
  country: string;
  phone: string | null;
  isDefault: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface AccountPaymentMethod {
  publicId: string;
  provider: string;
  tokenRef: string | null;
  label: string | null;
  brand: string;
  last4: string;
  expMonth: number | null;
  expYear: number | null;
  holderName: string | null;
  billingAddressPublicId: string | null;
  isDefault: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface UpdateProfileRequest {
  name?: string | null;
  nickname?: string | null;
  productAnalyticsEnabled?: boolean;
}

export interface ChangePasswordRequest {
  currentPassword: string;
  newPassword: string;
}

export interface AccountMfaMethod {
  publicId: string;
  type: 'totp' | 'email';
  label: string;
  enabled: boolean;
  enabledAt: string | null;
  lastUsedAt: string | null;
}

export interface AccountSecurityEvent {
  publicId: string;
  type: string;
  createdAt: string;
}

export interface AccountSecurityOverview {
  passwordEnabled: boolean;
  google: { connected: boolean; email: string | null; connectedAt: string | null };
  mfaMethods: AccountMfaMethod[];
  recoveryCodesRemaining: number;
  recentEvents: AccountSecurityEvent[];
}

export interface SecurityReauthentication {
  securityToken: string;
  expiresAt: string;
}

export interface TotpEnrollment {
  issuer: string;
  accountName: string;
  manualEntryKey: string;
  provisioningUri: string;
}

export interface CreateAddressRequest {
  kind?: 'shipping' | 'billing' | 'other';
  label?: string | null;
  fullName?: string | null;
  company?: string | null;
  line1: string;
  line2?: string | null;
  city: string;
  state?: string | null;
  postalCode: string;
  country: string;
  phone?: string | null;
  isDefault?: boolean;
}

export interface UpdateAddressRequest {
  kind?: 'shipping' | 'billing' | 'other';
  label?: string | null;
  fullName?: string | null;
  company?: string | null;
  line1?: string;
  line2?: string | null;
  city?: string;
  state?: string | null;
  postalCode?: string;
  country?: string;
  phone?: string | null;
  isDefault?: boolean;
}

export interface CreatePaymentMethodRequest {
  provider?: string;
  tokenRef?: string | null;
  label?: string | null;
  brand: string;
  last4: string;
  expMonth?: number | null;
  expYear?: number | null;
  holderName?: string | null;
  billingAddressPublicId?: string | null;
  isDefault?: boolean;
}

export interface UpdatePaymentMethodRequest {
  provider?: string;
  tokenRef?: string | null;
  label?: string | null;
  brand?: string;
  last4?: string;
  expMonth?: number | null;
  expYear?: number | null;
  holderName?: string | null;
  billingAddressPublicId?: string | null;
  isDefault?: boolean;
}

export interface DeleteAccountRequest {
  password: string;
  confirmation: 'DELETE';
}

interface ProfileResponse {
  success: boolean;
  user: AccountProfile;
}

interface AddressResponse {
  success: boolean;
  address: AccountAddress;
}

interface AddressListResponse {
  success: boolean;
  addresses: AccountAddress[];
}

interface PaymentMethodResponse {
  success: boolean;
  paymentMethod: AccountPaymentMethod;
}

interface PaymentMethodListResponse {
  success: boolean;
  paymentMethods: AccountPaymentMethod[];
}

interface MessageResponse {
  success: boolean;
  message: string;
}

interface SecurityResponse {
  success: boolean;
  security: AccountSecurityOverview;
}

@Injectable({
  providedIn: 'root',
})
export class AccountService {
  private readonly API_URL = environment.apiUrl;

  constructor(private http: HttpClient) {}

  getProfile(): Observable<AccountProfile> {
    return this.http
      .get<ProfileResponse>(`${this.API_URL}/account/profile`)
      .pipe(map((response) => response.user), catchError(this.handleError));
  }

  updateProfile(payload: UpdateProfileRequest): Observable<AccountProfile> {
    return this.http
      .patch<ProfileResponse>(`${this.API_URL}/account/profile`, payload)
      .pipe(map((response) => response.user), catchError(this.handleError));
  }

  changePassword(payload: ChangePasswordRequest): Observable<string> {
    return this.http
      .post<MessageResponse>(`${this.API_URL}/account/change-password`, payload)
      .pipe(map((response) => response.message), catchError(this.handleError));
  }

  listAddresses(): Observable<AccountAddress[]> {
    return this.http
      .get<AddressListResponse>(`${this.API_URL}/account/addresses`)
      .pipe(map((response) => response.addresses), catchError(this.handleError));
  }

  createAddress(payload: CreateAddressRequest): Observable<AccountAddress> {
    return this.http
      .post<AddressResponse>(`${this.API_URL}/account/addresses`, payload)
      .pipe(map((response) => response.address), catchError(this.handleError));
  }

  updateAddress(addressPublicId: string, payload: UpdateAddressRequest): Observable<AccountAddress> {
    return this.http
      .put<AddressResponse>(`${this.API_URL}/account/addresses/${addressPublicId}`, payload)
      .pipe(map((response) => response.address), catchError(this.handleError));
  }

  getSecurity(): Observable<AccountSecurityOverview> {
    return this.http
      .get<SecurityResponse>(`${this.API_URL}/account/security`)
      .pipe(map((response) => response.security), catchError(this.handleError));
  }

  reauthenticateSecurity(password: string): Observable<SecurityReauthentication> {
    return this.http
      .post<{ success: boolean } & SecurityReauthentication>(`${this.API_URL}/account/security/reauthenticate`, { password })
      .pipe(map(({ securityToken, expiresAt }) => ({ securityToken, expiresAt })), catchError(this.handleError));
  }

  beginTotpEnrollment(securityToken: string): Observable<TotpEnrollment> {
    return this.http
      .post<{ success: boolean } & TotpEnrollment>(`${this.API_URL}/account/security/mfa/totp/start`, { securityToken })
      .pipe(map(({ issuer, accountName, manualEntryKey, provisioningUri }) => ({ issuer, accountName, manualEntryKey, provisioningUri })), catchError(this.handleError));
  }

  confirmTotpEnrollment(securityToken: string, code: string): Observable<{ message: string; recoveryCodes: string[] }> {
    return this.http
      .post<{ success: boolean; message: string; recoveryCodes: string[] }>(`${this.API_URL}/account/security/mfa/totp/confirm`, { securityToken, code })
      .pipe(map(({ message, recoveryCodes }) => ({ message, recoveryCodes })), catchError(this.handleError));
  }

  startEmailMfaEnrollment(securityToken: string): Observable<{ enrollmentId: string; expiresAt: string }> {
    return this.http
      .post<{ success: boolean; enrollmentId: string; expiresAt: string }>(`${this.API_URL}/account/security/mfa/email/start`, { securityToken })
      .pipe(map(({ enrollmentId, expiresAt }) => ({ enrollmentId, expiresAt })), catchError(this.handleError));
  }

  confirmEmailMfaEnrollment(securityToken: string, enrollmentId: string, code: string): Observable<{ message: string; recoveryCodes: string[] }> {
    return this.http
      .post<{ success: boolean; message: string; recoveryCodes: string[] }>(`${this.API_URL}/account/security/mfa/email/confirm`, { securityToken, enrollmentId, code })
      .pipe(map(({ message, recoveryCodes }) => ({ message, recoveryCodes })), catchError(this.handleError));
  }

  disableMfaMethod(securityToken: string, methodId: string): Observable<string> {
    return this.http
      .post<MessageResponse>(`${this.API_URL}/account/security/mfa/disable`, { securityToken, methodId })
      .pipe(map((response) => response.message), catchError(this.handleError));
  }

  regenerateRecoveryCodes(securityToken: string): Observable<string[]> {
    return this.http
      .post<{ success: boolean; recoveryCodes: string[] }>(`${this.API_URL}/account/security/recovery-codes`, { securityToken })
      .pipe(map((response) => response.recoveryCodes), catchError(this.handleError));
  }

  startGoogleLink(securityToken: string): Observable<string> {
    return this.http
      .post<{ success: boolean; authorizationUrl: string }>(`${this.API_URL}/account/security/google/start`, { securityToken })
      .pipe(map((response) => response.authorizationUrl), catchError(this.handleError));
  }

  disconnectGoogle(securityToken: string): Observable<string> {
    return this.http
      .post<MessageResponse>(`${this.API_URL}/account/security/google/disconnect`, { securityToken })
      .pipe(map((response) => response.message), catchError(this.handleError));
  }

  deleteAddress(addressPublicId: string): Observable<string> {
    return this.http
      .delete<MessageResponse>(`${this.API_URL}/account/addresses/${addressPublicId}`)
      .pipe(map((response) => response.message), catchError(this.handleError));
  }

  listPaymentMethods(): Observable<AccountPaymentMethod[]> {
    return this.http
      .get<PaymentMethodListResponse>(`${this.API_URL}/account/payment-methods`)
      .pipe(map((response) => response.paymentMethods), catchError(this.handleError));
  }

  createPaymentMethod(
    payload: CreatePaymentMethodRequest
  ): Observable<AccountPaymentMethod> {
    return this.http
      .post<PaymentMethodResponse>(`${this.API_URL}/account/payment-methods`, payload)
      .pipe(map((response) => response.paymentMethod), catchError(this.handleError));
  }

  updatePaymentMethod(
    methodPublicId: string,
    payload: UpdatePaymentMethodRequest
  ): Observable<AccountPaymentMethod> {
    return this.http
      .put<PaymentMethodResponse>(
        `${this.API_URL}/account/payment-methods/${methodPublicId}`,
        payload
      )
      .pipe(map((response) => response.paymentMethod), catchError(this.handleError));
  }

  deletePaymentMethod(methodPublicId: string): Observable<string> {
    return this.http
      .delete<MessageResponse>(`${this.API_URL}/account/payment-methods/${methodPublicId}`)
      .pipe(map((response) => response.message), catchError(this.handleError));
  }

  deleteAccount(payload: DeleteAccountRequest): Observable<string> {
    return this.http
      .request<MessageResponse>('delete', `${this.API_URL}/account/delete-account`, {
        body: payload
      })
      .pipe(map((response) => response.message), catchError(this.handleError));
  }

  private handleError = (error: HttpErrorResponse): Observable<never> => {
    let message = 'An unexpected error occurred';

    if (error.error?.error?.message) {
      message = error.error.error.message as string;
    } else if (error.error?.message) {
      message = error.error.message as string;
    } else if (error.message) {
      message = error.message;
    }

    return throwError(() => ({
      code: error.status,
      message,
      details: error.error?.error?.details as string[] | undefined,
    } as AccountApiError));
  };
}
